// Raíz del router: conecta los ganchos globales del cliente HTTP con la navegación (401 → ingreso con
// `?volver`, 2FA pendiente, sesión restringida, módulo apagado) y registra el gesto para el sonido.
import { useEffect, useRef } from 'react';
import { Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/Toaster';
import { configureApi, setViewAsUserId } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { allowSheetLeave, saveOpenDrafts } from '@/lib/sheetGuard';
import { trackUserGesture } from '@/lib/sound';
import { loginPath } from './guards';

/** Rutas donde un 401 es esperable (no hay que redirigir). */
const PUBLIC_PREFIXES = ['/login', '/recuperar', '/restablecer', '/r/', '/r?'];

export function isPublicPath(pathname: string): boolean {
  return pathname === '/' || pathname === '/r' || PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

/** Evento para que una hoja abierta guarde su borrador antes de ir al ingreso (UX §2.8). */
export const SESSION_EXPIRED_EVENT = 'atte:session-expired';

export function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const locRef = useRef(location);
  useEffect(() => {
    locRef.current = location;
  });

  useEffect(() => trackUserGesture(), []);

  useEffect(() => {
    let redirecting = false;
    return configureApi({
      onUnauthorized: () => {
        const l = locRef.current;
        if (redirecting || isPublicPath(l.pathname)) return;
        redirecting = true;
        // UX §2.8: la hoja abierta guarda su borrador (se ofrece recuperarlo al volver a entrar).
        saveOpenDrafts();
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        allowSheetLeave();
        qc.removeQueries({ queryKey: qk.session });
        navigate(loginPath(`${l.pathname}${l.search}`, true), { replace: true });
        setTimeout(() => {
          redirecting = false;
        }, 1000);
      },
      onMfaPending: () => {
        if (locRef.current.pathname !== '/login/2fa') navigate('/login/2fa', { replace: true });
      },
      onRestricted: () => {
        void qc.invalidateQueries({ queryKey: qk.session });
      },
      onModuleDisabled: () => {
        // El toast y la recarga de la sesión los hace el QueryClient (lib/query.ts).
      },
      onViewAsInvalid: () => {
        setViewAsUserId(null);
        toast.warning('Esa vista ya no está disponible. Volviste a tu vista.');
        void qc.invalidateQueries();
      },
    });
  }, [navigate, qc]);

  return (
    <>
      <ScrollRestoration getKey={(l) => l.pathname} />
      <Outlet />
    </>
  );
}
